import { useState } from "react";
import { Link } from "react-router";
import { Heart } from "lucide-react";
import { DataUnavailable } from "@/design/DataUnavailable";
import type { BusinessCard } from "@/modules/discovery/surfaces";
import { BusinessCardTile } from "~/components/BusinessCardTile";
import { ConsumerPage } from "~/components/ConsumerChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { useQuery } from "~/lib/useQuery";

/**
 * FavoritesScreen (docs/ux/screens/FavoritesScreen.md), H35.
 *
 * The saved clinics, as the **same** business cards search renders, because a
 * saved clinic and a found clinic are the same thing. Most recently saved first:
 * the list is a memory, and the one saved a minute ago is the one being looked
 * for. No distance is shown, deliberately: distance is a search affordance and
 * this list is ordered by when things were saved.
 *
 * **Unhearting removes the card immediately and does not re-read.** This is the
 * one screen where the optimistic removal is right rather than risky: the
 * person is looking at a list *of* their hearts, so a card that stays after
 * being unhearted contradicts what they just did. The heart rolls its own fill
 * back if the write fails, and a refresh restores anything that did.
 */
export function FavoritesScreen() {
  const saved = useQuery<{ favorites: BusinessCard[] }>("/favorites");
  const [removed, setRemoved] = useState<Set<string>>(new Set());

  const cards = (saved.data?.favorites ?? []).filter(
    (card) => !removed.has(card.id),
  );

  return (
    <ConsumerPage>
      <div className="mx-auto w-full max-w-6xl px-5 py-8">
        <div className="mb-6 flex items-baseline justify-between gap-4">
          <h1 className="text-2xl font-extrabold text-ink sm:text-3xl">
            Saved clinics
          </h1>
          {cards.length > 0 && (
            <p className="text-sm text-muted">
              <span className="font-mono font-semibold text-ink">
                {cards.length}
              </span>{" "}
              {cards.length === 1 ? "clinic" : "clinics"}
            </p>
          )}
        </div>

        {saved.loading && !saved.data ? (
          <ScreenPending />
        ) : saved.failure ? (
          <DataUnavailable
            configured={saved.failure !== "not_configured"}
            what="your saved clinics"
          />
        ) : cards.length === 0 ? (
          <div className="rounded-2xl border border-line bg-surface px-5 py-16 text-center">
            <span className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-accent/12 text-accent">
              <Heart className="size-6" aria-hidden strokeWidth={1.8} />
            </span>
            <p className="mt-4 font-display text-lg font-bold text-ink">
              No saved clinics yet.
            </p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted">
              Tap the heart on any clinic to save it here.
            </p>
            <Link
              to="/search"
              className="mt-5 inline-flex font-medium text-accent hover:underline"
            >
              Find care
            </Link>
          </div>
        ) : (
          <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((card) => (
              <li key={card.id}>
                <BusinessCardTile
                  card={card}
                  favorite
                  signedIn
                  next="/favorites"
                  onFavoriteChange={(businessId, favorite) => {
                    if (favorite) return;
                    setRemoved((current) =>
                      new Set(current).add(businessId),
                    );
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </ConsumerPage>
  );
}
